var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { default as Webcam } from 'react-webcam';
import { Grid } from '@material-ui/core';
import { DeviceUtilitiesService, EMPTY_TEXT, FileInput, Magnetic, WhiteLabelMobileButton, useWindowSize } from '@mitchell/react.component.ux';
import { getIsIos, getIsMobileView, TranslationService } from "../../../common";
import { CAMERA_VIDEO_DIMENSIONS, IntelligentAssignmentSummary, IntelligentPhotoCaptureCameraConstants, PhotoCaptureConstants, PhotoCatureImageConstant, intelligentPhotoCaptureConstants } from '../../constants';
import { PhotoCaptureImageDescription } from '../../enums';
import { getAspectRatio, getGuidanceMessage, getImageQualityScore, photoCaptureAllowedMimeType } from '../../utilities';
import { StyledCameraFullView, StyledImageAnalysisMessage, StyledCameraGrid, StyledIconImage, StyledGridWebCamWrapper, StyledCameraButtonsView, StyledIconWrapper, StyledImage, StyledCaptureButton, StyledContentWrapper, HeaderContainer, HeaderDoneButtonWrapper, HeaderSubTitle } from './IntelligentImageAnalyzerCamera.style';
export const IntelligentGenericWebcamComponent = ({ onCapture, isImageAnalysisVisible = false, handleFileUpload, backgroundColor, headerText, shouldFlipCamera = false, shouldHideDeviceLibrary = false, setUIGuidanceScores, appTheme, fileDescription, handleDoneButton, isDoneButtonDisabled = false, shouldHideDoneButton }) => {
    const translateFn = TranslationService.translate;
    const videoPlayer = React.useRef(null);
    const flashTimeoutRef = React.useRef(null);
    const [streamObj, setStreamObj] = React.useState(null);
    const { cameraFlipIcon } = PhotoCatureImageConstant;
    const [iconImage, setIconImage] = React.useState(PhotoCaptureConstants.ALERT_FILL_CIRCLE_ICON_CLASS);
    const [cameraMode, setCameraMode] = React.useState(IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE);
    const [selectedCamera, setSelectedCamera] = React.useState({});
    const [canvasMessage, setCanvasMessage] = React.useState(IntelligentPhotoCaptureCameraConstants.ANALYZING_IMAGE_MESSAGE);
    const [isFlashActive, setIsFlashActive] = React.useState(false);
    const [browserWidth, browserHeight] = useWindowSize();
    const isPortrait = browserHeight > browserWidth;
    const gcdAspectRatio = getAspectRatio(browserWidth / browserHeight, 50);
    const deviceUtility = new DeviceUtilitiesService();
    const handleDevices = React.useCallback(mediaDevices => {
        if (getIsIos()) {
            if (deviceUtility.isPhone()) {
                const device = mediaDevices
                    .filter(({ kind, deviceId, label }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT && label.match(/Back Camera/))
                    .pop();
                setSelectedCamera(device);
            }
            else {
                const device = mediaDevices
                    .filter(({ kind, deviceId }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT)
                    .pop();
                setSelectedCamera(device);
            }
        }
        else if (deviceUtility.isMac()) {
            const device = mediaDevices
                .filter(({ kind, deviceId, label }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT && !label.match(/Desk View Camera/))
                .pop();
            setSelectedCamera(device);
        }
        else {
            setSelectedCamera(mediaDevices
                .filter(({ kind, deviceId }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT)
                .pop());
        }
    }, [setSelectedCamera]);
    React.useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [streamObj, handleDevices]);
    const isMobile = () => {
        if (getIsIos()) {
            return true;
        }
        return getIsMobileView();
    };
    /**
     * Asynchronously analyzes the quality of a screenshot captured from a live video feed.
     *
     * This function captures a screenshot from the `videoPlayer` element, then it sends the screenshot to an image quality analysis service.
     * Based on the result of the analysis, it updates the UI to provide feedback to the user. Specifically:
     * - If the image quality is clear, a success message and a "like" icon are displayed.
     * - If the image quality is not clear, a guidance message and an "exclamation" icon are shown.
     * - If an error occurs during the analysis, an error message is displayed with the details of the failure.
     *
     * The function uses asynchronous code (`async/await`) to wait for the result of the image analysis before updating the UI.
     *
     * @returns {Promise<void>} A Promise that resolves when the image analysis process is complete. No value is returned.
     */
    const getLiveVideoAnalyzer = () => __awaiter(void 0, void 0, void 0, function* () {
        // Capture a screenshot from the current video player
        const screenshot = videoPlayer.current && videoPlayer.current.getScreenshot();
        // If the screenshot was captured successfully, proceed with analysis
        if (screenshot) {
            try {
                // Perform image quality analysis asynchronously
                const { scores, isClear } = yield getImageQualityScore(screenshot);
                setUIGuidanceScores && setUIGuidanceScores(scores);
                if (isClear) {
                    // If clear, display a success message and a "like" icon
                    setCanvasMessage(IntelligentPhotoCaptureCameraConstants.IMAGE_CLEAR_MESSAGE);
                    setIconImage(PhotoCaptureConstants.LIKE_ICON_CLASS);
                }
                else {
                    // If not clear, display a guidance message and an "exclamation" icon
                    setCanvasMessage(getGuidanceMessage(scores));
                    setIconImage(PhotoCaptureConstants.ALERT_FILL_CIRCLE_ICON_CLASS);
                }
            }
            catch (error) {
                setCanvasMessage(IntelligentPhotoCaptureCameraConstants.ERROR_IN_ANALYSIS_MESSAGE + error.message);
            }
        }
    });
    /**
     * React `useEffect` hook that manages live video analysis and stream cleanup.
     *
     * When the stream is available (`streamObj.isStreamAvailable`) and image analysis is enabled (`isImageAnalysisVisible`),
     * it starts periodic image analysis (`getLiveVideoAnalyzer`) every 1 second.
     * The effect also cleans up by stopping the video tracks when the component unmounts or the stream is no longer available.
     *
     * @returns {void}
     */
    React.useEffect(() => {
        if (fileDescription === PhotoCaptureImageDescription.SUMMARY) {
            return undefined;
        }
        let analyzeInterval;
        if (streamObj && streamObj.isStreamAvailable && isImageAnalysisVisible) {
            analyzeInterval = setInterval(getLiveVideoAnalyzer, 1000);
        }
        return () => {
            if (streamObj && streamObj.stream) {
                analyzeInterval && clearInterval(analyzeInterval);
                streamObj.stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [streamObj, cameraMode, isImageAnalysisVisible, fileDescription]);
    const handleStreamAnalyzer = (stream) => {
        setStreamObj({ stream, isStreamAvailable: true });
    };
    const handleStreamAnalyzerError = (error) => {
        setCanvasMessage(IntelligentPhotoCaptureCameraConstants.ERROR_STREAM);
    };
    /**
     * Captures a screenshot from the video player and triggers the `onCapture` callback.
     *
     * If the `videoPlayer` element is available and contains a video, it attempts to capture a screenshot.
     * If the screenshot is successfully captured, it calls the `onCapture` function with the captured image.
     *
     * @returns {void}
     */
    const handleCameraCapture = () => {
        if (fileDescription === PhotoCaptureImageDescription.SUMMARY) {
            setIsFlashActive(true);
            // Clear any existing timeout before scheduling a new one
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }
            flashTimeoutRef.current = setTimeout(() => {
                setIsFlashActive(false);
                flashTimeoutRef.current = null;
            }, IntelligentPhotoCaptureCameraConstants.FLASH_ACTIVE_TIMEOUT_MS);
        }
        if (videoPlayer.current && videoPlayer.current.video) {
            const capturedImage = videoPlayer.current.getScreenshot();
            if (capturedImage) {
                onCapture(capturedImage);
            }
        }
    };
    React.useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
                flashTimeoutRef.current = null;
            }
        };
    }, []);
    const onChange = (filesList) => __awaiter(void 0, void 0, void 0, function* () {
        const files = filesList;
        if (files && files.length) {
            const imagefile = files[0];
            handleFileUpload && handleFileUpload(imagefile);
        }
    });
    const handleSwitchCameraMode = () => setCameraMode((cameraMode) => cameraMode === IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE
        ? IntelligentPhotoCaptureCameraConstants.FRONT_CAMERA_FACING_MODE
        : IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE);
    /**
     * Determines the alignment value based on camera flip, device library visibility,
     * and whether alignment is needed.
     *
     * @param {boolean} isAlignment - A flag to decide the alignment value ("center" or a numerical value).
     * @returns {string} - The alignment value based on the conditions.
     */
    const getAlignment = (isAlignment) => {
        if (shouldFlipCamera && !shouldHideDeviceLibrary) {
            return isAlignment ? 'center' : 4;
        }
        else if (shouldFlipCamera && shouldHideDeviceLibrary) {
            return isAlignment ? 'flex-end' : 6;
        }
        else if (!shouldFlipCamera && !shouldHideDeviceLibrary) {
            return isAlignment ? 'center' : 6;
        }
        return isAlignment ? 'center' : 12; // Default value if no condition matches
    };
    const divImageWrapper = () => {
        return (React.createElement(StyledIconWrapper, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-upload-button`, className: PhotoCaptureConstants.ADD_PHOTO_ICON_CLASS }));
    };
    return (React.createElement(StyledContentWrapper, { bgcolor: backgroundColor },
        React.createElement(StyledCameraFullView, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-intelligent-webcam`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-intelligent-webcam`, aspectratio: gcdAspectRatio[0] / gcdAspectRatio[1] },
            React.createElement(HeaderContainer, null, fileDescription === PhotoCaptureImageDescription.SUMMARY ? (React.createElement(React.Fragment, null, !shouldHideDoneButton && (React.createElement(HeaderDoneButtonWrapper, null,
                React.createElement(WhiteLabelMobileButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-summary-done-button`, label: translateFn(IntelligentAssignmentSummary.DONE_BUTTON_LABEL), buttonBackgroundColor: isDoneButtonDisabled
                        ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonBackgroundColor) || Magnetic.colorBrand50
                        : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonBackgroundColor) || Magnetic.colorBrand50, buttonTextColor: isDoneButtonDisabled
                        ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonTextColor) || Magnetic.colorWhite
                        : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonTextColor) || Magnetic.colorWhite, isDisabled: isDoneButtonDisabled, onClick: () => handleDoneButton && handleDoneButton() }))))) : (React.createElement(React.Fragment, null,
                React.createElement(HeaderSubTitle, { id: "camera_HeaderSubTitle" }, headerText && translateFn(headerText)),
                streamObj && streamObj.isStreamAvailable && isImageAnalysisVisible && (React.createElement(StyledImageAnalysisMessage, { container: true, wrap: "nowrap", justify: "center", alignItems: "flex-start" },
                    React.createElement(StyledIconImage, { className: iconImage }),
                    translateFn(canvasMessage)))))),
            React.createElement(Grid, { container: true, justify: "center", direction: "column" },
                React.createElement(StyledGridWebCamWrapper, { container: true, item: true, isFlashActive: isFlashActive },
                    React.createElement(Webcam, { id: 'webcam-component', ref: videoPlayer, onUserMedia: handleStreamAnalyzer, onUserMediaError: handleStreamAnalyzerError, audio: false, screenshotFormat: "image/jpeg", screenshotQuality: 1, imageSmoothing: true, videoConstraints: {
                            deviceId: { exact: selectedCamera === null || selectedCamera === void 0 ? void 0 : selectedCamera.deviceId },
                            aspectRatio: isMobile() && isPortrait ? browserHeight / browserWidth : browserWidth / browserHeight
                        }, width: browserWidth, height: browserHeight, minScreenshotHeight: CAMERA_VIDEO_DIMENSIONS.MIN_HEIGHT, minScreenshotWidth: CAMERA_VIDEO_DIMENSIONS.MIN_WIDTH })),
                React.createElement(StyledCameraButtonsView, { container: true, wrap: "nowrap" },
                    !shouldHideDeviceLibrary && (React.createElement(Grid, { item: true, xs: (shouldFlipCamera && 4) || 6 },
                        React.createElement(FileInput, { acceptedMimeTypes: photoCaptureAllowedMimeType, disabled: false, multiple: false, onSelected: onChange, element: divImageWrapper(), id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-fileInput` }))),
                    React.createElement(Grid, { id: "alignment-grid", item: true, container: shouldFlipCamera || shouldHideDeviceLibrary, alignItems: shouldFlipCamera || shouldHideDeviceLibrary ? 'center' : undefined, justify: getAlignment(true), xs: getAlignment(false) }, fileDescription === PhotoCaptureImageDescription.SUMMARY ? (React.createElement(WhiteLabelMobileButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, label: EMPTY_TEXT, className: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_CLASS_NAME, buttonBackgroundColor: isFlashActive
                            ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonBackgroundColor) || Magnetic.colorBrand50
                            : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonBackgroundColor) || Magnetic.colorBrand50, buttonTextColor: isFlashActive
                            ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonTextColor) || Magnetic.colorWhite
                            : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonTextColor) || Magnetic.colorWhite, isDisabled: isFlashActive, onClick: handleCameraCapture, icon: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_ICON })) : (React.createElement(StyledCaptureButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, icon: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_ICON, className: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_CLASS_NAME, onClick: handleCameraCapture, isHoverEffectDisabled: true }))),
                    shouldFlipCamera && (React.createElement(Grid, { item: true, container: true, alignItems: "center", justify: "flex-end", xs: (shouldHideDeviceLibrary && 6) || 4 },
                        React.createElement(StyledIconWrapper, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-flip-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-flip-button`, onClick: handleSwitchCameraMode },
                            React.createElement(StyledImage, { src: cameraFlipIcon.image })))))),
            !streamObj && (React.createElement(StyledCameraGrid, { container: true, justify: "center", alignItems: "center", direction: "column" }, translateFn(IntelligentPhotoCaptureCameraConstants.NO_STREAM_ERROR))))));
};
//# sourceMappingURL=IntelligentGenericWebcamComponent.js.map